import React, { useState, useEffect } from "react";
import { Link as RouterLink, useLocation, matchPath } from "react-router-dom";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";

import { bgBlur } from "../../utils/cssStyles";
import Logo from "../logo";
import { loginRequest } from "../../services/authConfig";
import { callMsGraph } from "../../services/graph";
import { SignOutButton } from "./SignOutButton";
import { useCart } from "../../context/CartContext";

// MTS appbar color (mirrors fitter dashboard header).
const APPBAR_COLOR = "#f5a623";
const HEADER_H = 64;

const NAV_ITEMS = [
  { label: "Shop", to: "/shop", icon: <StorefrontOutlinedIcon /> },
  { label: "Basket", to: "/basket", icon: <ShoppingCartOutlinedIcon />, isBasket: true },
  { label: "Pending Orders", to: "/pendingorders", icon: <PendingActionsOutlinedIcon /> },
  { label: "Posted Orders", to: "/orders", icon: <LocalShippingOutlinedIcon /> },
  { label: "Invoices", to: "/invoices", icon: <ReceiptLongOutlinedIcon /> },
];

const PAGE_TITLES = [
  { path: "/shop", title: "Shop" },
  { path: "/basket", title: "Basket" },
  { path: "/item/:id", title: "Item" },
  { path: "/pendingorders", title: "Pending Orders" },
  { path: "/orders", title: "Posted Orders" },
  { path: "/invoices", title: "Invoices" },
  { path: "/profile", title: "Profile" },
];

function initialsOf(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return parts[0].slice(0, 2).toUpperCase();
}

export const Header = () => {
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();

  const isAuthenticated = useIsAuthenticated();
  const { cartItemCount } = useCart();
  const { instance, accounts } = useMsal();

  const [graphData, setGraphData] = useState(null);
  const [accountAnchor, setAccountAnchor] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const userName = accounts && accounts[0] ? accounts[0].name : "";
  const env =
    (typeof window !== "undefined" && window.__APP_CONFIG__?.BUILD_ID) || "";
  const isDev = String(env).toLowerCase().includes("dev");

  useEffect(() => {
    if (isAuthenticated && accounts.length > 0 && !graphData) {
      instance
        .acquireTokenSilent({ ...loginRequest, account: accounts[0] })
        .then((response) =>
          callMsGraph(response.accessToken).then((resp) => setGraphData(resp))
        )
        .catch(() => {});
    }
  }, [isAuthenticated, accounts, graphData, instance]);

  const displayTitle =
    PAGE_TITLES.find((p) => matchPath(p.path, location.pathname))?.title ||
    "MTS Shop";

  const isActive = (to) =>
    !to.startsWith("http") && location.pathname.startsWith(to);

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          height: HEADER_H,
          color: "#1a1a1a",
          boxShadow: "none",
          ...bgBlur({ color: APPBAR_COLOR }),
        }}
      >
        <Toolbar sx={{ height: 1, px: { xs: 1.5, lg: 5 }, minHeight: HEADER_H }}>
          {isAuthenticated && isCompact && (
            <IconButton
              onClick={() => setDrawerOpen(true)}
              sx={{ mr: 1, color: "#1a1a1a" }}
              aria-label="Open navigation"
              edge="start"
            >
              <MenuIcon />
            </IconButton>
          )}

          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ flexShrink: 0 }}
          >
            <Logo sx={{ width: 32, height: 32 }} />
          </Stack>

          {isAuthenticated && !isCompact && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{ ml: 3 }}
            >
              {NAV_ITEMS.filter((n) => !n.isBasket).map((item) => {
                const active = isActive(item.to);
                return (
                  <Button
                    key={item.label}
                    {...(item.external
                      ? { component: "a", href: item.to, target: "_blank", rel: "noopener" }
                      : { component: RouterLink, to: item.to })}
                    sx={{
                      color: "#1a1a1a",
                      fontWeight: active ? 700 : 500,
                      fontSize: 14,
                      letterSpacing: 0.2,
                      textTransform: "none",
                      px: 1.5,
                      py: 0.75,
                      borderRadius: 1.5,
                      bgcolor: active ? "rgba(255,255,255,0.55)" : "transparent",
                      "&:hover": {
                        bgcolor: active
                          ? "rgba(255,255,255,0.7)"
                          : "rgba(255,255,255,0.35)",
                      },
                    }}
                  >
                    {item.label}
                  </Button>
                );
              })}
            </Stack>
          )}

          <Stack
            flexGrow={1}
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            spacing={{ xs: 1, sm: 1.5 }}
            sx={{ minWidth: 0 }}
          >
            {isDev && (
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 800,
                  color: "#c8102e",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  bgcolor: "rgba(255,255,255,0.6)",
                  px: 0.75,
                  py: 0.25,
                  borderRadius: 999,
                  flexShrink: 0,
                }}
              >
                dev
              </Typography>
            )}

            {isCompact && (
              <Typography
                sx={{
                  fontSize: { xs: 14, sm: 16 },
                  fontWeight: 700,
                  color: "#1a1a1a",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  minWidth: 0,
                }}
              >
                {displayTitle}
              </Typography>
            )}

            {isAuthenticated && (
              <>
                <IconButton
                  component={RouterLink}
                  to="/basket"
                  sx={{ color: "#1a1a1a" }}
                  aria-label="Basket"
                >
                  <Badge
                    color="error"
                    badgeContent={cartItemCount}
                    max={99}
                    overlap="circular"
                  >
                    <ShoppingCartOutlinedIcon />
                  </Badge>
                </IconButton>

                <IconButton
                  onClick={(e) => setAccountAnchor(e.currentTarget)}
                  sx={{
                    p: 0.5,
                    borderRadius: 999,
                    "&:hover": { bgcolor: "rgba(0,0,0,0.08)" },
                  }}
                  aria-label="Account"
                >
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      bgcolor: "#fff",
                      color: "#1a1a1a",
                      fontSize: 13,
                      fontWeight: 700,
                      border: "2px solid #1a1a1a",
                    }}
                  >
                    {initialsOf(userName) || (
                      <PersonOutlineOutlinedIcon fontSize="small" />
                    )}
                  </Avatar>
                </IconButton>
              </>
            )}
          </Stack>
        </Toolbar>
      </AppBar>

      <Menu
        anchorEl={accountAnchor}
        open={Boolean(accountAnchor)}
        onClose={() => setAccountAnchor(null)}
        PaperProps={{ sx: { width: 240, mt: 1 } }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Box sx={{ px: 2, py: 1.75 }}>
          <Typography variant="subtitle2" noWrap>
            {userName || "Account"}
          </Typography>
          {graphData?.mail && (
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{ display: "block" }}
            >
              {graphData.mail}
            </Typography>
          )}
        </Box>
        <Divider />
        <MenuItem
          component={RouterLink}
          to="/profile"
          onClick={() => setAccountAnchor(null)}
        >
          <PersonOutlineOutlinedIcon
            fontSize="small"
            sx={{ mr: 1.5, color: "text.secondary" }}
          />
          Profile
        </MenuItem>
        <Divider />
        <SignOutButton onClick={() => setAccountAnchor(null)} />
      </Menu>

      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 280 } }}
      >
        <Box
          sx={{
            px: 2,
            py: 2,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            ...bgBlur({ color: APPBAR_COLOR }),
            color: "#1a1a1a",
          }}
        >
          <Logo sx={{ width: 32, height: 32 }} disabledLink />
          <Typography sx={{ fontWeight: 800, fontSize: 16 }}>
            Midland Tyre Services
          </Typography>
        </Box>
        <List sx={{ pt: 1 }}>
          {NAV_ITEMS.map((item) => {
            const active = isActive(item.to);
            const button = (
              <ListItemButton
                key={item.label}
                {...(item.external
                  ? { component: "a", href: item.to, target: "_blank", rel: "noopener" }
                  : { component: RouterLink, to: item.to })}
                onClick={() => setDrawerOpen(false)}
                selected={active}
                sx={{
                  mx: 1,
                  borderRadius: 1.5,
                  mb: 0.5,
                  "&.Mui-selected": {
                    bgcolor: "rgba(245, 166, 35, 0.16)",
                    color: "#1a1a1a",
                    fontWeight: 700,
                    "&:hover": { bgcolor: "rgba(245, 166, 35, 0.24)" },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36, color: "inherit" }}>
                  {item.isBasket ? (
                    <Badge
                      color="error"
                      badgeContent={cartItemCount}
                      max={99}
                      overlap="circular"
                    >
                      {item.icon}
                    </Badge>
                  ) : (
                    item.icon
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontWeight: active ? 700 : 500,
                    fontSize: 14,
                  }}
                />
              </ListItemButton>
            );
            return button;
          })}
        </List>
      </Drawer>
    </>
  );
};
